<template>
  <div v-loading="loading">
    <template v-if="invite">
      <AuthHeading
        title="You're invited to a team"
        :sub-title="`${invite.inviterEmail} added you to their team on Eventor.app.`"
      />
      <div class="bg-gray-100 rounded-8 p-20 mb-25">
        <p class="text-xs text-gray-600 tracking-wide uppercase">
          Your role
        </p>
        <p class="font-bold text-lg text-primary leading-tight mt-2">
          {{ invite.roleName }}
        </p>
        <p
          class="flex items-center text-sm text-gray-700 mt-12"
          :title="`Sign-in email: ${invite.email}`"
        >
          <FontAwesomeIcon
            :icon="['fas', 'envelope']"
            fixed-width
            class="flex-shrink-0 text-gray-500 mr-8"
          />
          <span
            data-private
            class="truncate"
          >{{ invite.email }}</span>
        </p>
      </div>
      <ElButton
        type="primary"
        class="w-full"
        :disabled="accepting"
        @click="onAccept"
      >
        {{ accepting ? 'Signing you in..' : 'Accept and sign in' }}
      </ElButton>
      <p class="text-sm text-center text-primary-light leading-snug mt-20">
        Or
        <RouterLink
          :to="{ name: 'AuthSignIn' }"
          class="text-primary hover:underline"
        >sign in with your email and password</RouterLink>.
      </p>
    </template>
    <template v-else-if="error">
      <AuthHeading
        title="This invitation has expired"
        :sub-title="error"
      />
      <ElButton
        type="primary"
        class="w-full"
        @click="$router.replace({ name: 'AuthSignIn' })"
      >
        Go to sign in
      </ElButton>
    </template>
  </div>
</template>

<script>
import AuthHeading from '@/components/auth/AuthHeading';
import { FBAuth } from '@/helpers/firebaseServices';
import { APIAcceptInvite, APIGetInviteInfo } from '@/api';
import { showErrorNotification, showSuccessNotification } from '@/utils/notification';

/**
 * The page the "Accept and sign in" button in a team invitation email opens.
 *
 * Loading this page consumes nothing, and that is the whole design. Gmail, Google Workspace
 * and Outlook ATP prefetch links in incoming mail, so a magic link that is spent by a GET is
 * dead before the recipient's thumb comes down — exactly what happened to the Firebase
 * oobCode in AuthManagement.vue, which was "routinely spent before the customer ever clicks".
 * Here the GET only reads who invited whom (APIGetInviteInfo, which does not mutate), and the
 * invitation is spent by APIAcceptInvite, a POST this page makes after somebody taps the
 * button. Scanners issue GET and do not run this code.
 *
 * The backend tolerates a few exchanges for ten minutes on top of that, because a phone opens
 * the link in the mail app's own webview and the person then taps "open in Safari".
 */
export default {
  components: {
    AuthHeading,
  },
  data: () => ({
    // Held here and nowhere else: see created(), which takes it out of the URL immediately.
    token: '',
    invite: null,
    error: '',
    loading: true,
    accepting: false,
  }),
  async created() {
    this.token = this.$route.query.t || '';

    // Out of the address bar before anything else happens. A token left in the URL travels in
    // the Referer header of every request the page makes, into analytics, into LogRocket's
    // session URL and into the browser's history and sync. replaceState does not re-run the
    // router, so the component keeps the copy it just took.
    if (this.token && window.history?.replaceState) {
      window.history.replaceState({}, document.title, this.$route.path);
    }

    if (!this.token) {
      this.$router.replace({ name: 'AuthSignIn' });
      return;
    }

    try {
      const { data } = await APIGetInviteInfo({ token: this.token });
      this.invite = data;
    } catch (error) {
      this.error = this.readableError(error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    readableError(error) {
      return (error.response && error.response.data && error.response.data.message)
        || 'This invitation link could not be opened. Please sign in with your email address and password.';
    },
    async onAccept() {
      this.accepting = true;
      try {
        const { data } = await APIAcceptInvite({ token: this.token });
        // plugins/firebaseAuth.js listens on onAuthStateChanged and fills the store for every
        // sign-in method, so there is nothing to commit here.
        await FBAuth.signInWithCustomToken(data.customToken);

        const { dataLayer } = window;
        if (dataLayer) dataLayer.push({ event: 'team-invitation-accepted' });

        this.$router.replace({ name: 'Dashboard' });
        showSuccessNotification('Welcome to the team.');
      } catch (error) {
        this.accepting = false;
        // The invitation may have died between loading the page and tapping the button, so
        // the page has to be able to go from "here is your invitation" to "it is gone".
        this.invite = null;
        this.error = this.readableError(error);
        showErrorNotification(this.error, 8000);
      }
    },
  },
};
</script>

<style scoped>
</style>
